import { useMemo } from 'react'

import { TeamMemberRole } from '@api'

import { RemovableBadge } from '@/modules/creators/ui/RemovableBadge'
import { FormItem } from '@/modules/shifts/ui/create-edit-form/form-item'
import { MultiSelect } from '@/modules/shifts/ui/dialogs/multi-select'

import { useFetchTeamTable } from '../../hooks/use-fetch-team-table'

interface Props {
  selectedMemberIds: number[]
  onChange: (selectedMemberIds: number[]) => void
  disabled?: boolean
}

export const MemberSelection = ({
  selectedMemberIds,
  disabled,
  onChange,
}: Props) => {
  const { data: members } = useFetchTeamTable()
  const selectedMembers = useMemo(
    () => members.filter((member) => selectedMemberIds.includes(member.id)),
    [members, selectedMemberIds],
  )

  const memberOptions = useMemo(
    () =>
      members
        .filter((member) => member.role !== TeamMemberRole.ADMIN)
        .map((member) => ({
          label: member.name,
          value: member.id.toString(),
        })),
    [members],
  )

  const handleRemove = (id: number) => {
    const newSelectedMemberIds = selectedMemberIds.filter(
      (selectedId) => selectedId !== id,
    )

    onChange(newSelectedMemberIds)
  }

  const handleChange = (value: string[]) => {
    onChange(value.map(Number))
  }

  return (
    <div className="flex flex-col gap-1">
      <FormItem label="Select members to add this role to">
        <MultiSelect
          disabled={!memberOptions.length || disabled}
          options={memberOptions}
          value={selectedMemberIds.map((id) => id.toString())}
          onChange={handleChange}
          entity="Team Members"
          placeholder="Assign Team Member"
          searchPlaceholder="Search Team Member"
        />
      </FormItem>
      {selectedMembers.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {selectedMembers.map((member) => {
            return (
              <RemovableBadge
                name={member.name}
                onRemove={handleRemove}
                key={member.id}
                value={member.id}
                disabled={disabled}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
